import { z } from 'zod'

import { ApiClientError } from '@ems/api-client/client'

import { api } from '#admin/lib/api-client.ts'

/**
 * The exhibitor companies and their members (COMPANY_MEMBERS_PLAN.md §5). A member can do all
 * that the applicant can do in the portal. Every member is equal: there are no roles.
 */
/** The kinds of company, in the order the form shows them (COMPANY_PROFILE_PLAN.md decision 3). */
export const COMPANY_KINDS = ['exhibitor', 'sponsor', 'visitor', 'media', 'organizer'] as const
export type CompanyKind = (typeof COMPANY_KINDS)[number]

export const KIND_LABELS: Record<CompanyKind, string> = {
  exhibitor: 'Exhibitor',
  sponsor: 'Sponsor',
  visitor: 'Visitor',
  media: 'Media',
  organizer: 'Organizer',
}

export const companySchema = z.object({
  id: z.string(),
  name: z.string(),
  // `.default(…)`: the console is deployed ahead of the API, whose answer may not carry them yet.
  displayName: z.string().nullable().default(null),
  kinds: z.array(z.string()).default(['exhibitor']),
  countryCode: z.string().nullable().default(null),
  logoKey: z.string().nullable().default(null),
  memberCount: z.number(),
})

const addressSchema = z.object({
  line1: z.string().nullable(),
  line2: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  postalCode: z.string().nullable(),
})

/** The full profile of a company, as v1 had it. The team route returns it with the members. */
export const companyProfileSchema = z.object({
  id: z.string(),
  name: z.string(),
  displayName: z.string().nullable(),
  kinds: z.array(z.enum(COMPANY_KINDS)),
  description: z.string().nullable(),
  industrySector: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  fax: z.string().nullable(),
  taxId: z.string().nullable(),
  countryCode: z.string().nullable(),
  website: z.string().nullable(),
  logoKey: z.string().nullable(),
  address: addressSchema.nullable(),
})

/** A member's `name` is their email until a registration names them. */
export const memberSchema = z.object({
  personId: z.string(),
  name: z.string(),
  email: z.string(),
  addedAt: z.string(),
})

export const teamSchema = z.object({
  company: companyProfileSchema,
  members: z.array(memberSchema),
})

export type Company = z.infer<typeof companySchema>
export type CompanyProfile = z.infer<typeof companyProfileSchema>
export type Member = z.infer<typeof memberSchema>

const companiesBase = '/v1/exhibitor/admin/companies'

/** Only the companies of kind `exhibitor`, sorted by name. */
export function fetchCompanies(signal?: AbortSignal): Promise<{ items: Company[] }> {
  return api.get(companiesBase, {
    schema: z.object({ items: z.array(companySchema) }),
    ...(signal ? { signal } : {}),
  })
}

/** The full profile in one request. Refused with `conflict` for a name in use. */
export function createCompany(body: ProfileBody) {
  return api.post(companiesBase, { schema: companySchema, body })
}

/** Refused with `conflict` while the company has an application that is not rejected or canceled. */
/** Replaces the kinds of a company, and nothing else: a PATCH keeps every absent field. */
export function setCompanyKinds(companyId: string, kinds: readonly CompanyKind[]) {
  return api.patch(`${companiesBase}/${companyId}`, {
    schema: z.object({ id: z.string() }),
    body: { kinds },
  })
}

export function deleteCompany(companyId: string) {
  return api.delete(`${companiesBase}/${companyId}`, {
    schema: z.object({ deleted: z.boolean() }),
  })
}

export function fetchTeam(companyId: string, signal?: AbortSignal) {
  return api.get(`${companiesBase}/${companyId}/members`, {
    schema: teamSchema,
    ...(signal ? { signal } : {}),
  })
}

/** A PATCH, sent whole: `null` clears a field. Refused with `conflict` for a name in use. */
export function updateCompanyProfile(companyId: string, body: ProfileBody) {
  return api.patch(`${companiesBase}/${companyId}`, { schema: companyProfileSchema, body })
}

/** A short-lived URL for one company logo: saved, or uploaded and not saved yet. */
export function fetchCompanyLogoUrl(key: string, signal?: AbortSignal): Promise<string> {
  return api
    .get(`${companiesBase}/logo?${new URLSearchParams({ key }).toString()}`, {
      schema: z.object({ url: z.string() }),
      ...(signal ? { signal } : {}),
    })
    .then((response) => response.url)
}

/** The person gets an email. A person who is already in this company stays, with no change. */
export function addMember(companyId: string, email: string) {
  return api.post(`${companiesBase}/${companyId}/members`, {
    schema: memberSchema,
    body: { email },
  })
}

export function removeMember(companyId: string, personId: string) {
  return api.delete(`${companiesBase}/${companyId}/members/${personId}`, {
    schema: z.object({ removed: z.boolean() }),
  })
}

/** True when the person is in another company of this edition. One person has one company. */
export function isAlreadyInCompany(error: unknown): error is ApiClientError {
  return (
    error instanceof ApiClientError &&
    error.code === 'conflict' &&
    error.detail?.['reason'] === 'already_in_company'
  )
}

// --- the initials in an empty logo slot -------------------------------------------------------

/** The legal forms that start an Indonesian company name. They tell nothing about the company. */
const LEGAL_PREFIXES = new Set(['PT', 'CV', 'UD', 'PD'])

/**
 * Two letters for a company with no logo: the first letters of its first two words, after a
 * legal form such as "PT" or "CV". A name of one word gives its first two letters, and a name that
 * is only a legal form gives the form. Empty when the name has no letters.
 *
 * "PT Pertamina Geothermal Energy" → "PG". "Marubeni Corporation" → "MC".
 */
export function companyInitials(name: string): string {
  // Each word as its letters, one string for each letter, so a letter outside the basic plane
  // stays whole.
  const words = name
    .split(/\s+/)
    .map((word) => word.match(/\p{L}/gu) ?? [])
    .filter((letters) => letters.length > 0)
  let start = 0
  while (
    start < words.length - 1 &&
    LEGAL_PREFIXES.has(words[start]?.join('').toUpperCase() ?? '')
  ) {
    start += 1
  }
  const [first = [], second] = words.slice(start)
  const letters = second === undefined ? first.slice(0, 2) : [first[0], second[0]]
  return letters.join('').toUpperCase()
}

// --- the profile form ---------------------------------------------------------------------------

/** What the form edits: each input as the control holds it, `''` for nothing. The address is flat. */
export type ProfileDraft = {
  name: string
  displayName: string
  kinds: CompanyKind[]
  description: string
  industrySector: string
  email: string
  phone: string
  fax: string
  website: string
  taxId: string
  countryCode: string
  logoKey: string
  line1: string
  line2: string
  city: string
  state: string
  postalCode: string
}

export type ProfileField = keyof ProfileDraft

export type ProfileBody = Omit<CompanyProfile, 'id'>

/** The draft of a new company: an exhibitor, with nothing else. */
export const EMPTY_DRAFT: ProfileDraft = {
  name: '',
  displayName: '',
  kinds: ['exhibitor'],
  description: '',
  industrySector: '',
  email: '',
  phone: '',
  fax: '',
  website: '',
  taxId: '',
  countryCode: '',
  logoKey: '',
  line1: '',
  line2: '',
  city: '',
  state: '',
  postalCode: '',
}

/** The draft of a saved company, or of a new one for `null`. */
export function draftOf(company: CompanyProfile | null): ProfileDraft {
  if (company === null) return EMPTY_DRAFT
  const address = company.address
  return {
    name: company.name,
    displayName: company.displayName ?? '',
    kinds: COMPANY_KINDS.filter((kind) => company.kinds.includes(kind)),
    description: company.description ?? '',
    industrySector: company.industrySector ?? '',
    email: company.email ?? '',
    phone: company.phone ?? '',
    fax: company.fax ?? '',
    website: company.website ?? '',
    taxId: company.taxId ?? '',
    countryCode: company.countryCode ?? '',
    logoKey: company.logoKey ?? '',
    line1: address?.line1 ?? '',
    line2: address?.line2 ?? '',
    city: address?.city ?? '',
    state: address?.state ?? '',
    postalCode: address?.postalCode ?? '',
  }
}

const orNull = (value: string) => (value.trim() === '' ? null : value.trim())

/** The wire body. An emptied input is `null`, which clears its column rather than keeping it. */
export function bodyOf(draft: ProfileDraft): ProfileBody {
  return {
    name: draft.name.trim(),
    displayName: orNull(draft.displayName),
    kinds: draft.kinds,
    description: orNull(draft.description),
    industrySector: orNull(draft.industrySector),
    email: orNull(draft.email),
    phone: orNull(draft.phone),
    fax: orNull(draft.fax),
    taxId: orNull(draft.taxId),
    countryCode: orNull(draft.countryCode),
    website: orNull(draft.website),
    logoKey: orNull(draft.logoKey),
    address: {
      line1: orNull(draft.line1),
      line2: orNull(draft.line2),
      city: orNull(draft.city),
      state: orNull(draft.state),
      postalCode: orNull(draft.postalCode),
    },
  }
}

/** What is wrong with a draft, by field. Empty when it can be sent. The server checks all too. */
export function draftIssues(draft: ProfileDraft): Partial<Record<ProfileField, string>> {
  const issues: Partial<Record<ProfileField, string>> = {}
  const email = draft.email.trim()
  const website = draft.website.trim()
  if (draft.name.trim() === '') issues.name = 'Enter the name of the company.'
  if (!draft.kinds.includes('exhibitor')) issues.kinds = 'Keep the kind "Exhibitor".'
  if (email !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    issues.email = 'Enter an email address, such as info@example.com.'
  }
  if (website !== '' && !isWebAddress(website)) {
    issues.website = 'Enter the full address, starting with https://.'
  }
  return issues
}

function isWebAddress(value: string): boolean {
  return URL.canParse(value) && /^https?:$/.test(new URL(value).protocol)
}

/**
 * Which field a refusal belongs beside. A name in use is `conflict`. The logo check names
 * `detail.field`. The schema validator lists `detail.issues`, whose path names the field; an
 * address part is the second part of its path. `undefined` sends the refusal to a toast.
 */
export function refusalField(error: unknown): ProfileField | undefined {
  if (!(error instanceof ApiClientError)) return undefined
  if (error.code === 'conflict') return 'name'
  if (error.code !== 'validation_failed') return undefined
  const detail = error.detail ?? {}
  const issues = detail['issues']
  const path = Array.isArray(issues) ? pathOf(issues[0]) : [detail['field']]
  const named = path[0] === 'address' ? path[1] : path[0]
  return isField(named) ? named : undefined
}

const FIELDS: readonly ProfileField[] = [
  'name',
  'displayName',
  'kinds',
  'description',
  'industrySector',
  'email',
  'phone',
  'fax',
  'website',
  'taxId',
  'countryCode',
  'logoKey',
  'line1',
  'line2',
  'city',
  'state',
  'postalCode',
]

function isField(value: unknown): value is ProfileField {
  return FIELDS.some((field) => field === value)
}

function pathOf(issue: unknown): unknown[] {
  if (typeof issue !== 'object' || issue === null || !('path' in issue)) return []
  return Array.isArray(issue.path) ? issue.path : []
}
